import { getRequest, getRequestDownloadFile } from 'Services/baseRequests/getRequest'
import {
  UpdateUserInformationPayload,
  BulkUserManagementPayload,
  BulkUserManagementResponse,
  GetUsersForEmployerApiResponse,
  GetUsersForEmployerResponse,
  InviteUserApiPayload,
  InviteUserPayload,
  ListUsersWithMetadataResponse,
  UserInformationUpdate,
  RemoveUserPayload,
  RemoveUserResponse,
  RequestResponse
} from '../apiResponseTypes'
import { formatInviteUserPayload, parseGetUsersForEmployerData } from 'Services/users/parsers'
import { postRequest } from 'Services/baseRequests/postRequest'
import { deleteRequest } from 'Services/baseRequests/deleteRequest'
import { putRequest } from 'Services/baseRequests/putRequest'
import { parseErrorResponse } from 'Services/baseRequests/shared'
import { UserStatus } from 'Utils/types/userStatus'
import { UserRole } from 'Utils/types/roles'
import { Order } from 'Utils/helpers/tables'
import { CountryCode } from 'Utils/types/countries'

export type GetUserForEmployerPayload = {
  employerId: number
  perPage: number
  pageNumber: number
  status?: UserStatus
  benefitProgramId?: number
  roles?: UserRole[]
  searchText?: string
  sortBy?: string
  sortOrder?: Order
  country?: CountryCode
}

/**
 * Returns a list of the users for the given employer
 * @param payload
 */
export const getUsersForEmployer = async (
  payload: GetUserForEmployerPayload
): Promise<GetUsersForEmployerResponse> => {
  const {
    employerId,
    perPage,
    pageNumber,
    status,
    benefitProgramId,
    roles,
    searchText,
    sortOrder,
    sortBy,
    country
  } = payload

  const response = await getRequest<GetUsersForEmployerApiResponse>(
    [
      `/employers/${employerId}/users/list_for_employer?`,
      `per_page=${perPage}`,
      `&page=${pageNumber}`,
      `&status=${status || ''}`,
      `&benefit_programs=${benefitProgramId || ''}`,
      `&country_code=${country || ''}`,
      `${roles && roles.length > 0 ? roles.map((r) => `&role[]=${r}`).join('') : ''}`,
      `&search_text=${searchText || ''}`,
      `&sort_order=${sortOrder || ''}`,
      `&sort_by=${sortBy || ''}`
    ].join('')
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data ? parseGetUsersForEmployerData(response.data) : undefined,
    usersCount: response?.data?.total_records ?? 0
  }
}

/**
 * Invite a user to use JOON for given employer
 * @param payload The details of the user being invited
 */
export const inviteUser = async ({
  users,
  employerId
}: InviteUserPayload): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, InviteUserApiPayload>(
    `/employers/${employerId}/users/invites/invite_multiple`,
    formatInviteUserPayload(users)
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.success ? '' : parseErrorResponse(response.data) ?? ''
  }
}

/**
 * Download user management CSV template for given employer
 * @param employerId The employer id
 */
export const downloadUserManagementTemplate = async (employerId: number): Promise<Blob> => {
  const response = await getRequestDownloadFile(
    `/employers/${employerId}/export_user_management_template`,
    {
      responseType: 'blob'
    }
  )

  if (!response?.data) {
    throw new Error('Failed to download template')
  }

  return new Blob([response.data])
}

/**
 * Bulk user management via CSV upload (with dry-run support)
 * Handles creating new users, updating existing users, and detecting removals
 * @param payload The csv file and dry_run flag
 */
export const bulkUserManagement = async ({
  employerId,
  formData,
  dryRun = false,
  fullReplaceMode = false
}: BulkUserManagementPayload): Promise<BulkUserManagementResponse> => {
  const params = new URLSearchParams()
  if (dryRun) params.append('dry_run', 'true')
  if (fullReplaceMode) params.append('full_replace_mode', 'true')
  const queryString = params.toString()
  const url = `/employers/${employerId}/bulk_user_management${queryString ? `?${queryString}` : ''}`
  const response = await postRequest<BulkUserManagementResponse, FormData>(url, formData)

  return response
}

/**
 * Get all users with their metadata for given employer
 * @param employerId The employer id
 */
export const getUsersWithMetadata = async (
  employerId: number
): Promise<ListUsersWithMetadataResponse> =>
  await getRequest<ListUsersWithMetadataResponse>(`/employers/${employerId}/users_with_metadata`)

/**
 * Updates names and metadata for one or more users
 * @param payload The employer id and array of updates
 */
export const updateUserInformation = async ({
  employerId,
  updates
}: UpdateUserInformationPayload): Promise<RequestResponse> =>
  await postRequest<RequestResponse, { updates: UserInformationUpdate[] }>(
    `/employers/${employerId}/update_user_information`,
    { updates }
  )

/**
 * Re-invites a user to use JOON for given employer
 * @param payload The userId of the user being re-invited
 */
export const reinviteUser = async (payload: { userId: number }): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, { user_id: number }>(`/invites/reinvite`, {
    user_id: payload.userId
  })

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

/**
 * Reset login attempt
 * @param payload The id of the user
 */
export const resetLogin = async (payload: { userId: number }): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse>(
    `/admin/employees/${payload.userId}/reset_login`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

/**
 * Cancels the given invite if it is active
 * @param payload The invite being cancelled
 */
export const cancelUserInvite = async (payload: { userId: number }): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, { user_id: number }>(`/invites/cancel`, {
    user_id: payload.userId
  })

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

/**
 * Sets the user to REMOVED and updates everything as needed
 * @param payload The user being removed and the employer they are being removed from
 */
export const removeUser = async (payload: RemoveUserPayload): Promise<RemoveUserResponse> => {
  const response = await deleteRequest<RequestResponse>(
    `/employers/${payload.employerId}/users/${payload.userId}?end_time=${payload.endTime}`
  )

  return {
    statusCode: response.statusCode,
    userId: response?.data
  }
}

/**
 * Grants the given user the EmployerAdmin role.
 * @param payload The id of the user having permissions added.
 */
export const grantUserAdminPermissions = async (payload: {
  userId: number
}): Promise<RequestResponse> => {
  const response = await putRequest<RequestResponse>(
    `/admin/employees/${payload.userId}/add_administrative_permission`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

/**
 * Revokes admin permissions from the given user.
 * @param payload The id of the user having permissions removed.
 */
export const revokeUserAdminPermissions = async (payload: {
  userId: number
}): Promise<RequestResponse> => {
  const response = await putRequest<RequestResponse>(
    `/admin/employees/${payload.userId}/remove_administrative_permission`
  )

  return {
    statusCode: response.statusCode,
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}
